import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, BarChart3, ChevronDown, ChevronUp } from 'lucide-react';
import { useSelector } from 'react-redux';
import { createCalculationModelsApi } from './api';
import ProjectResultsChart from './ProjectResultsChart';
import { activePrimaryBinding, versionIdOf } from './projectCalculationModel';
import {
  consolidateExecutionOutput,
  calculateVariation,
  deduplicateExecutionWarnings,
  executionPeriod,
  economicImpactPayload,
  inclusiveDayCount,
  manualVariablesFromGraph,
  outputsFromGraph,
  suggestedComparisonPeriods,
} from './projectResults';

const QUALITY_LABELS = { good: 'Datos completos', partial: 'Datos parciales', adjusted: 'Datos ajustados', missing: 'Datos faltantes', invalid: 'Datos inválidos' };
const numberFormat = new Intl.NumberFormat('es-AR', { maximumFractionDigits: 3 });
const formatValue = value => numberFormat.format(Number(value));
const moneyFormat = (value, currency) => new Intl.NumberFormat('es-AR', {
  style: 'currency', currency: currency || 'ARS', maximumFractionDigits: 2,
}).format(Number(value));

export default function ProjectResultsSection({ project, refreshKey = 0, onResultChange }) {
  const token = useSelector(state => state.user.currentUser?.accessToken);
  const authenticated = Boolean(token);
  const api = useMemo(() => createCalculationModelsApi(token), [token]);
  const apiRef = useRef(api);
  const loadedContextIdentityRef = useRef(null);
  const hasLoadedContextRef = useRef(false);
  const runningRef = useRef(false);
  const automaticExecutionRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [context, setContext] = useState(null);
  const [error, setError] = useState('');
  const [selectedOutput, setSelectedOutput] = useState('');
  const [periods, setPeriods] = useState(() => suggestedComparisonPeriods(project));
  const [manualValues, setManualValues] = useState({});
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [failureWarnings, setFailureWarnings] = useState([]);
  const [showWarnings, setShowWarnings] = useState(false);
  const [editingPeriods, setEditingPeriods] = useState(false);

  useEffect(() => { apiRef.current = api; }, [api]);

  useEffect(() => {
    setPeriods(suggestedComparisonPeriods(project));
    setResult(null);
    setFailureWarnings([]);
    setError('');
    setShowWarnings(false);
    setEditingPeriods(false);
    automaticExecutionRef.current = null;
  }, [project?.actual_end_date, project?.id, project?.planned_end_date, project?.start_date]);

  const applyLoadedContextIdentity = useCallback(nextIdentity => {
    if (hasLoadedContextRef.current && loadedContextIdentityRef.current !== nextIdentity) {
      setResult(null);
      setFailureWarnings([]);
      setError('');
      setShowWarnings(false);
    }
    loadedContextIdentityRef.current = nextIdentity;
    hasLoadedContextRef.current = true;
  }, []);

  const load = useCallback(async () => {
    if (!authenticated || !project?.id) return;
    setLoading(true); setError('');
    try {
      const bindings = await apiRef.current.projectBindings(project.id);
      const binding = activePrimaryBinding(bindings);
      if (!binding || binding.version?.status !== 'published') {
        applyLoadedContextIdentity(null);
        setContext(null);
        return;
      }
      const modelId = Number(binding.model_id || binding.model?.id);
      const versionId = Number(binding.version_id || versionIdOf(binding.version));
      if (!modelId || !versionId) {
        applyLoadedContextIdentity(null);
        setContext(null);
        return;
      }
      const [graph, devices] = await Promise.all([apiRef.current.version(modelId, versionId), apiRef.current.devices()]);
      if (graph.status !== 'published') {
        applyLoadedContextIdentity(null);
        setContext(null);
        return;
      }
      const sourceDeviceIds = (graph.nodes || []).filter(node => ['source.basic_parameter', 'source.legacy_calculated_parameter'].includes(node.type)).map(node => Number(node.config_json?.device_id));
      const sourceOutsideScope = sourceDeviceIds.some(deviceId => {
        const device = (Array.isArray(devices) ? devices : []).find(item => Number(item.id) === deviceId);
        return !device || (project.company_id != null && Number(device.company_fk) !== Number(project.company_id))
          || (project.branch_id != null && Number(device.branch_fk) !== Number(project.branch_id));
      });
      const outputs = outputsFromGraph(graph);
      const variables = manualVariablesFromGraph(graph);
      applyLoadedContextIdentity(`${project.id}:${modelId}:${versionId}`);
      setContext({ binding, graph, modelId, versionId, outputs, variables, sourceOutsideScope });
      setSelectedOutput(current => outputs.some(output => output.key === current) ? current : outputs[0]?.key || '');
    } catch (_) {
      setContext(null);
      setError('No pudimos preparar los resultados del proyecto. Intentá nuevamente.');
    } finally { setLoading(false); }
  }, [applyLoadedContextIdentity, authenticated, project?.branch_id, project?.company_id, project?.id, refreshKey]);

  useEffect(() => { load(); }, [load]);

  const output = context?.outputs.find(item => item.key === selectedOutput);
  const referenceDays = inclusiveDayCount(periods.referenceStart, periods.referenceEnd);
  const evaluationDays = inclusiveDayCount(periods.evaluationStart, periods.evaluationEnd);
  const periodError = !periods.referenceStart || !periods.referenceEnd || !periods.evaluationStart || !periods.evaluationEnd
    ? 'Completá los dos períodos para calcular los resultados.'
    : !referenceDays || !evaluationDays
      ? 'Revisá las fechas: la fecha desde debe ser anterior o igual a la fecha hasta.'
      : referenceDays !== evaluationDays
        ? 'Los períodos deben tener la misma duración para compararlos directamente.'
        : '';
  const missingVariable = context?.variables.find(variable => variable.required && (manualValues[variable.key] === '' || manualValues[variable.key] == null || !Number.isFinite(Number(manualValues[variable.key]))));
  const unsupported = output && output.semantics.shape === 'series' && !output.semantics.reducible;
  const scopeError = context?.sourceOutsideScope ? 'Una de las mediciones no pertenece a la empresa o sucursal del proyecto.' : '';

  const calculate = async () => {
    if (runningRef.current || periodError || missingVariable || !output || unsupported || scopeError) return;
    runningRef.current = true; setRunning(true); setError(''); setResult(null); setFailureWarnings([]); setShowWarnings(false);
    const manualVariables = Object.fromEntries((context.variables || [])
      .filter(variable => manualValues[variable.key] !== '' && manualValues[variable.key] != null)
      .map(variable => [variable.key, Number(manualValues[variable.key])]));
    const payloadFor = (start, end) => ({ ...executionPeriod(start, end), frequency: 'day', manualVariables });
    const [referenceResponse, evaluationResponse] = await Promise.allSettled([
      apiRef.current.execute(context.modelId, context.versionId, payloadFor(periods.referenceStart, periods.referenceEnd)),
      apiRef.current.execute(context.modelId, context.versionId, payloadFor(periods.evaluationStart, periods.evaluationEnd)),
    ]);
    if (referenceResponse.status === 'rejected' || evaluationResponse.status === 'rejected') {
      const friendlyFailure = (label, response) => {
        if (response.status !== 'rejected') return null;
        const message = response.reason?.message || 'No se pudo completar la ejecución.';
        if (/alcance|empresa|sucursal|dispositivo fuente/i.test(message)) return `${label}: una de las mediciones no pertenece a la empresa o sucursal del proyecto.`;
        if (/sin datos|no hay mediciones|no se encontraron mediciones/i.test(message)) return `${label}: no hay mediciones disponibles para el período seleccionado.`;
        return `${label}: ${message}`;
      };
      setError([friendlyFailure('Período de referencia', referenceResponse), friendlyFailure('Período evaluado', evaluationResponse)].filter(Boolean).join(' '));
    } else {
      const executionWarnings = deduplicateExecutionWarnings([
        ...(referenceResponse.value.warnings || []).map(warning => ({ ...warning, period: 'Referencia' })),
        ...(evaluationResponse.value.warnings || []).map(warning => ({ ...warning, period: 'Período evaluado' })),
      ]);
      const reference = consolidateExecutionOutput(referenceResponse.value.outputs?.[output.key], output.semantics);
      const evaluation = consolidateExecutionOutput(evaluationResponse.value.outputs?.[output.key], output.semantics);
      if (!reference.valid || !evaluation.valid) {
        const messages = [!reference.valid && `Referencia: ${reference.message}`, !evaluation.valid && `Período evaluado: ${evaluation.message}`].filter(Boolean);
        setFailureWarnings(executionWarnings);
        setError(messages.join(' '));
      } else if (reference.unit !== evaluation.unit) {
        setError('Los resultados de ambos períodos no tienen la misma unidad y no pueden compararse.');
      } else {
        const { absolute, percentage } = calculateVariation(reference.value, evaluation.value);
        const nextResult = {
          reference, evaluation, absolute, percentage, unit: reference.unit,
          referenceOutput: referenceResponse.value.outputs?.[output.key],
          evaluationOutput: evaluationResponse.value.outputs?.[output.key],
          warnings: executionWarnings,
          economicImpact: null,
        };
        setResult(nextResult);
        try {
          const economicImpact = await apiRef.current.projectEconomicImpact(project.id, economicImpactPayload({
            evaluationStart: periods.evaluationStart,
            evaluationEnd: periods.evaluationEnd,
            energyVariation: absolute,
            energyUnit: reference.unit,
          }));
          setResult(current => current === nextResult ? { ...current, economicImpact } : current);
        } catch (economicError) {
          setResult(current => current === nextResult ? {
            ...current,
            economicImpact: {
              status: 'unavailable',
              message: economicError?.message || 'No se pudo obtener el contexto tarifario del período evaluado.',
              warnings: [],
            },
          } : current);
        }
      }
    }
    runningRef.current = false; setRunning(false);
  };

  const automaticExecutionKey = context && output
    ? `${context.modelId}:${context.versionId}:${selectedOutput}:${periods.referenceStart}:${periods.referenceEnd}:${periods.evaluationStart}:${periods.evaluationEnd}`
    : null;

  useEffect(() => {
    if (
      !automaticExecutionKey
      || editingPeriods
      || result
      || error
      || runningRef.current
      || periodError
      || missingVariable
      || unsupported
      || scopeError
      || context?.variables.length > 0
      || automaticExecutionRef.current === automaticExecutionKey
    ) return;
    automaticExecutionRef.current = automaticExecutionKey;
    calculate();
  }, [automaticExecutionKey, context?.variables.length, editingPeriods, error, missingVariable, periodError, result, scopeError, unsupported]);

  useEffect(() => {
    if (!onResultChange) return;
    onResultChange(result ? {
      outputName: output?.name || selectedOutput,
      evaluation: result.evaluation.value,
      absolute: result.absolute,
      percentage: result.percentage,
      unit: result.unit,
      quality: result.evaluation.quality,
      periods,
    } : null);
  }, [onResultChange, output?.name, periods, result, selectedOutput]);

  return <section className="projects-card project-results-section">
    <div className="projects-card-header projects-detail-section-header">
      <div><h2>Resultados del proyecto</h2><p>Compará el mismo indicador antes y después de la mejora.</p></div>
      <BarChart3 size={24} />
    </div>
    {loading ? <div className="project-results-state">Preparando resultados…</div> : !context ? <div className="project-results-empty"><strong>Todavía no hay un cálculo del impacto publicado.</strong><p>Configurá y publicá el cálculo para poder comparar resultados del proyecto.</p>{error && <small>{error}</small>}</div> : <div className="project-results-body">
      {context.outputs.length > 1 && <label className="project-results-field project-results-output"><span>Resultado a comparar</span><select value={selectedOutput} onChange={event => { setSelectedOutput(event.target.value); setResult(null); setError(''); }}>{context.outputs.map(item => <option key={item.key} value={item.key}>{item.name}</option>)}</select></label>}
      {context.outputs.length === 1 && <div className="project-results-indicator"><span>Indicador</span><strong>{output?.name}</strong></div>}
      {!context.outputs.length && <div className="project-results-empty"><strong>El cálculo publicado no tiene resultados configurados.</strong></div>}
      {output && <>
        {!editingPeriods && <div className="project-results-period-summary">
          <div><span>Período de referencia</span><strong>{periods.referenceStart.split('-').reverse().join('/')} → {periods.referenceEnd.split('-').reverse().join('/')}</strong></div>
          <div><span>Período evaluado</span><strong>{periods.evaluationStart.split('-').reverse().join('/')} → {periods.evaluationEnd.split('-').reverse().join('/')}</strong></div>
          <button type="button" className="projects-btn projects-btn-secondary" onClick={() => setEditingPeriods(true)}>Cambiar períodos</button>
        </div>}
        {editingPeriods && <div className="project-results-periods">
          <fieldset><legend>Período de referencia</legend><p>Base contra la cual se realiza la comparación.</p><div><label className="project-results-field"><span>Desde</span><input type="date" value={periods.referenceStart} onChange={event => { setPeriods(current => ({ ...current, referenceStart: event.target.value })); setResult(null); setError(''); }} /></label><label className="project-results-field"><span>Hasta</span><input type="date" value={periods.referenceEnd} onChange={event => { setPeriods(current => ({ ...current, referenceEnd: event.target.value })); setResult(null); setError(''); }} /></label></div></fieldset>
          <fieldset><legend>Período evaluado</legend><p>Período cuyo resultado querés evaluar.</p><div><label className="project-results-field"><span>Desde</span><input type="date" value={periods.evaluationStart} onChange={event => { setPeriods(current => ({ ...current, evaluationStart: event.target.value })); setResult(null); setError(''); }} /></label><label className="project-results-field"><span>Hasta</span><input type="date" value={periods.evaluationEnd} onChange={event => { setPeriods(current => ({ ...current, evaluationEnd: event.target.value })); setResult(null); setError(''); }} /></label></div></fieldset>
        </div>}
        {context.variables.length > 0 && <div className="project-results-variables"><h3>Valores necesarios para el cálculo</h3><p>Se usarán los mismos valores en ambos períodos.</p><div>{context.variables.map(variable => <label className="project-results-field" key={variable.key}><span>{variable.name}{variable.required ? ' *' : ''}</span><div><input type="number" step="any" value={manualValues[variable.key] ?? ''} onChange={event => { setManualValues(current => ({ ...current, [variable.key]: event.target.value })); setResult(null); setError(''); }} />{variable.unit !== '1' && <em>{variable.unit}</em>}</div></label>)}</div></div>}
        {unsupported && <div className="project-results-message is-warning"><AlertTriangle size={18} /><span>{output.semantics.reason} Elegí otro resultado o ajustá cómo se resume la medición en el cálculo.</span></div>}
        {scopeError && <div className="project-results-message is-error">{scopeError} Corregí el cálculo antes de comparar resultados.</div>}
        {!unsupported && periodError && <div className="project-results-message">{periodError}</div>}
        {!unsupported && !periodError && missingVariable && <div className="project-results-message">Completá “{missingVariable.name}” para ejecutar la comparación.</div>}
        {error && <div className="project-results-message is-error">{error}</div>}
        {error && failureWarnings.length > 0 && <div className="project-results-warning"><div><AlertTriangle size={18} /><span>La ejecución informó advertencias sobre las mediciones.</span></div><button type="button" onClick={() => setShowWarnings(value => !value)}>{showWarnings ? <ChevronUp size={15} /> : <ChevronDown size={15} />}{showWarnings ? 'Ocultar detalle' : 'Ver detalle'}</button>{showWarnings && <ul>{failureWarnings.map((warning, index) => <li key={`${warning.period}-${warning.code}-${index}`}><strong>{warning.period}:</strong> {warning.message || warning.code}</li>)}</ul>}</div>}
        {running && !result && <div className="project-results-state is-inline">Calculando resultados automáticamente…</div>}
        {(editingPeriods || context.variables.length > 0 || Boolean(error)) && <div className="project-results-actions">
          <button type="button" className="projects-btn projects-btn-primary project-results-run" disabled={running || Boolean(periodError) || Boolean(missingVariable) || Boolean(unsupported) || Boolean(scopeError)} onClick={() => { setEditingPeriods(false); automaticExecutionRef.current = automaticExecutionKey; calculate(); }}>{running ? 'Calculando resultados…' : result || error ? 'Volver a calcular' : 'Calcular resultados'}</button>
        </div>}
        {result && <div className="project-results-result">
          <h3>{output.name}</h3>
          <ProjectResultsChart referenceOutput={result.referenceOutput} evaluationOutput={result.evaluationOutput} unit={result.unit} />
          <div className="project-results-values"><div><span>Referencia</span><strong>{formatValue(result.reference.value)} <small>{result.unit}</small></strong><em className={`quality-${result.reference.quality}`}>{QUALITY_LABELS[result.reference.quality]}</em></div><div><span>Período evaluado</span><strong>{formatValue(result.evaluation.value)} <small>{result.unit}</small></strong><em className={`quality-${result.evaluation.quality}`}>{QUALITY_LABELS[result.evaluation.quality]}</em></div></div>
          <div className="project-results-variation"><span>Variación</span><strong>{result.absolute > 0 ? '+' : ''}{formatValue(result.absolute)} <small>{result.unit}</small></strong><b>{result.percentage == null ? 'No disponible' : `${result.percentage > 0 ? '+' : ''}${formatValue(result.percentage)} %`}</b></div>
          <div className={`project-results-economic ${result.economicImpact?.quality === 'partial' ? 'is-partial' : ''}`}>
            <div className="project-results-economic-heading">
              <div><span>Impacto económico estimado</span>{result.economicImpact == null
                ? <strong>Calculando…</strong>
                : result.economicImpact.status === 'estimated'
                  ? <strong>{moneyFormat(result.economicImpact.economic_impact.estimated_amount, result.economicImpact.economic_impact.currency)}</strong>
                  : <strong>No disponible</strong>}</div>
              {result.economicImpact?.coverage?.coverage_ratio != null && <em>Cobertura tarifaria: {formatValue(result.economicImpact.coverage.coverage_ratio * 100)} %</em>}
            </div>
            {result.economicImpact == null ? <p>Obteniendo el precio medio de energía del período evaluado.</p> : result.economicImpact.status === 'estimated' ? <>
              <dl>
                <div><dt>Precio medio aplicado</dt><dd>{moneyFormat(result.economicImpact.tariff_context.unit_cost, result.economicImpact.tariff_context.currency)} / kWh</dd></div>
                <div><dt>Período tarifario</dt><dd>{periods.evaluationStart.split('-').reverse().join('/')} → {periods.evaluationEnd.split('-').reverse().join('/')}</dd></div>
              </dl>
              <p>Estimación basada únicamente en energía activa facturada, con prorrateo según el período evaluado.</p>
            </> : <p>{result.economicImpact?.message || 'No fue posible obtener información tarifaria suficiente para este período.'}</p>}
            {(result.economicImpact?.warnings || []).filter(warning => warning !== result.economicImpact?.message).length > 0 && <div className="project-results-economic-warnings"><AlertTriangle size={17} /><ul>{result.economicImpact.warnings.filter(warning => warning !== result.economicImpact?.message).map((warning, index) => <li key={index}>{warning}</li>)}</ul></div>}
          </div>
          {(result.reference.degraded || result.evaluation.degraded || result.warnings.length > 0) && <div className="project-results-warning"><div><AlertTriangle size={18} /><span>Se detectaron advertencias o ajustes en algunas mediciones.</span></div>{result.warnings.length > 0 && <button type="button" onClick={() => setShowWarnings(value => !value)}>{showWarnings ? <ChevronUp size={15} /> : <ChevronDown size={15} />}{showWarnings ? 'Ocultar detalle' : 'Ver detalle'}</button>}{showWarnings && <ul>{result.warnings.map((warning, index) => <li key={`${warning.period}-${warning.code}-${index}`}><strong>{warning.period}:</strong> {warning.message || warning.code}</li>)}</ul>}</div>}
        </div>}
      </>}
    </div>}
  </section>;
}
